import { useMemo, useCallback, useState } from 'react';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../utils/constants/commonConstants';

/**
 * Manages consolidated/standalone financial type selection.
 *
 * Lifecycle:
 * 1. Consumer fetches data → API response includes `hasConsolidated` / `hasStandalone` flags.
 * 2. Consumer calls `setAuditedTypeFromFlags(hConsolidated, hStandalone)`.
 *    - On first call (when `auditedType` is still `null`), this derives and sets the initial dropdown value.
 *    - On subsequent calls, only the flags update (dropdown stays as the user chose).
 * 3. User picks from dropdown → `handleConsolidationChange(val)` updates `auditedType` and calls `onConsolidationChange`.
 * 4. `onConsolidationChange(nature)` fires only on user-driven changes, giving the consumer
 *    the uppercase nature string (e.g. `'CONSOLIDATED'`) to re-fetch data.
 *
 * @param onConsolidationChange — called with the uppercase nature string when the user
 *   explicitly changes the dropdown. Not called on initial load or programmatic updates.
 */

type AuditedTypeOption = { misDisplayName: string; isDataAvailable: boolean };

interface UseAuditedTypeReturn {
    auditedType: string | null;
    hasConsolidated: boolean;
    hasStandalone: boolean;
    auditedTypeOptionsObject: AuditedTypeOption[];
    handleConsolidationChange: (val: string) => void;
    setAuditedTypeFromFlags: (hConsolidated: boolean, hStandalone: boolean) => void;
}

export function useAuditedType(onConsolidationChange?: (nature: string) => void): UseAuditedTypeReturn {
    const [auditedType, setAuditedType] = useState<string | null>(null);
    const [hasConsolidated, setHasConsolidated] = useState(false);
    const [hasStandalone, setHasStandalone] = useState(false);

    const auditedTypeOptionsObject = useMemo<AuditedTypeOption[]>(
        () => [
            { misDisplayName: AUDITED_TYPE_CONSOLIDATED, isDataAvailable: hasConsolidated },
            { misDisplayName: AUDITED_TYPE_STANDALONE, isDataAvailable: hasStandalone },
        ],
        [hasConsolidated, hasStandalone],
    );

    /**
     * Called with API-returned flags. Sets the dropdown value only on initial load
     * (when `auditedType` is still `null`). Does NOT trigger `onConsolidationChange`.
     */
    const setAuditedTypeFromFlags = useCallback((hConsolidated: boolean, hStandalone: boolean) => {
        setHasConsolidated(hConsolidated);
        setHasStandalone(hStandalone);
        setAuditedType((prev) => {
            if (prev !== null) return prev;
            if (hConsolidated) return AUDITED_TYPE_CONSOLIDATED;
            if (hStandalone) return AUDITED_TYPE_STANDALONE;
            return null;
        });
    }, []);

    /**
     * Called when user picks from dropdown. Validates availability, updates state,
     * and fires `onConsolidationChange` with the uppercase nature string.
     */
    const handleConsolidationChange = useCallback(
        (val: string) => {
            setAuditedType((prev) => {
                if (val === prev) return prev;
                const isAvailable = val === AUDITED_TYPE_CONSOLIDATED ? hasConsolidated : hasStandalone;
                if (!isAvailable) return prev;
                onConsolidationChange?.(val.toUpperCase());
                return val;
            });
        },
        [hasConsolidated, hasStandalone, onConsolidationChange],
    );

    return {
        auditedType,
        hasConsolidated,
        hasStandalone,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromFlags,
    };
}

/** Convert a display value (e.g. 'Consolidated') to the API nature string ('CONSOLIDATED'). */
export const toNature = (type: string | null): string | undefined => type?.toUpperCase() ?? undefined;
